"use client";

/**
 * Comparable vendors — alternatives considered for this opportunity, and why
 * each was not selected.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`comps`
 * section): `.rv-intro` for the lead sentence, `.rv-comptop` / `.rv-compcat`
 * for the vendor name and category line, `.rv-compnot` for the "Not selected
 * because" label, and `.rv-compreason` for each ✕-marked reason. The capture
 * line reuses `.rv-attrib` — plan §5 gap #10 notes Scope and Comparable
 * vendors share one `Attrib`, not two independent ones.
 */

import { X } from "../lib/icons";
import type { ReportAttribution, ReportComparableVendor } from "./report-types";

export interface ReportComparableVendorsProps {
  vendors: ReportComparableVendor[];
  attribution: ReportAttribution | null;
}

/**
 * `.rv-card.rv-rows` — one white bordered container holding every vendor as a
 * `.rv-row`. The `.rv-rows` variant zeroes the card's own 18px padding so each
 * row carries it and the dividers run edge to edge.
 */
const RV_CARD =
  "bg-(--bg-white) border border-(--border-subtle) rounded-(--radius-container) mb-3 overflow-hidden";

/** `.rv-row` — 16px/18px padding with a hairline rule above every row but the first. */
const RV_ROW =
  "px-[18px] py-4 border-t border-(--border-subtle) first:border-t-0";

/** `.rv-intro` — the section's lead sentence, above the vendor list. */
const INTRO_CLASS = "m-0 mb-3 text-[14px] leading-[1.6] text-(--fg-secondary)";

/** `.rv-attrib` — small subtle "Captured by X · date" meta line. */
const ATTRIBUTION_CLASS = "mt-3 text-[11.5px] leading-[1.5] text-(--fg-subtle)";

/**
 * One vendor: `.rv-comptop` (`.rv-rowname` + `.rv-compcat`), then
 * `.rv-compnot` + the `.rv-compreason` list — omitted entirely when
 * `reasonsNotSelected` is empty, per plan §5 gap #12.
 */
function VendorRow({ vendor }: { vendor: ReportComparableVendor }) {
  return (
    <div className={RV_ROW}>
      {/* .rv-comptop — `.rv-rowname` drops its 260px basis here. */}
      <div className="mb-[9px] flex items-baseline gap-2.5">
        <span className="text-[13.5px] font-medium text-(--fg-muted)">
          {vendor.name}
        </span>
        {vendor.category && (
          <span className="text-[11px] text-(--fg-muted)">
            {vendor.category}
          </span>
        )}
      </div>
      {vendor.reasonsNotSelected.length > 0 && (
        <>
          <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.08em] text-(--fg-muted)">
            Not selected because
          </div>
          <ul className="m-0 flex list-none flex-col p-0">
            {vendor.reasonsNotSelected.map((reason) => (
              <li
                key={reason}
                className="flex items-start gap-2 py-0.5 text-[12.5px] leading-[1.55] text-(--fg-secondary)"
              >
                <span className="mt-[3px] shrink-0 text-(--fg-subtle)">
                  <X size={12} />
                </span>
                {reason}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * No comparable vendors exist on real data today (plan §5 gap #12) —
 * alternatives considered during intake, and the reasons each was rejected,
 * aren't captured as structured fields yet.
 */
function ComparableVendorsEmptyState() {
  return (
    <div
      className={[
        "bg-(--bg-canvas) border border-dashed border-(--border-default)",
        "rounded-(--radius-container) p-[18px]",
      ].join(" ")}
    >
      <p className="m-0 text-[13px] leading-[1.6] text-(--fg-secondary)">
        Comparable vendors are not captured on this opportunity yet. Products
        considered as alternatives, and the reasons each was not selected,
        aren't modelled as structured fields today.
      </p>
    </div>
  );
}

/**
 * The `comps` report section: an intro line, one card per comparable vendor,
 * and the shared capture attribution.
 */
export function ReportComparableVendors({
  vendors,
  attribution,
}: ReportComparableVendorsProps) {
  return (
    <div>
      {vendors.length > 0 ? (
        <>
          <p className={INTRO_CLASS}>
            Other products considered for this opportunity, and why each was not
            selected.
          </p>
          <div className={RV_CARD}>
            {vendors.map((vendor) => (
              <VendorRow key={vendor.id} vendor={vendor} />
            ))}
          </div>
        </>
      ) : (
        <ComparableVendorsEmptyState />
      )}
      {attribution && (
        <p className={ATTRIBUTION_CLASS}>
          Captured by {attribution.capturedBy} · {attribution.capturedAt}
        </p>
      )}
    </div>
  );
}
